import { sb, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabase';
import { getAnonId } from './claimFunnel';
import { noteLanding, recordSignupAttribution } from './attribution';
import { isInternalReferrer, isOwnFlowReturn } from './internalReferrers';

// Growth OS web tracker (step 2, owner-approved 2026-11-01). Sends events to
// track_events() (20261101900000_growth_events.sql), which owns every rule that
// matters — allowed event names and keys, masking, dedupe, rate limits. This
// file only collects, batches and retries.
//
// It must NEVER break a page: every public function swallows its own errors.
//
// Identity: anon_id is the claim funnel's per-browser id (localStorage
// maabar.anonId), so analytics_internal_actors keeps filtering team browsers.
// user_id is never sent — the server takes it from the signed-in session.
//
// Attribution: UTMs and click ids ride on session_started, per session. The
// 30-day first touch and last non-direct touch live in src/lib/attribution.js
// (owner, 2026-11-01) and are saved at sign-up and on every store order.

const SESSION_KEY = 'maabar.growth.session';
const QUEUE_KEY = 'maabar.growth.queue';
const IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 5000;
const BATCH = 50;        // track_events() refuses more than 50 per call
const QUEUE_MAX = 200;   // drop the oldest beyond this rather than grow forever
const ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'ttclid'];
const APP_VERSION = (process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || '').slice(0, 7) || 'web';

// Team pages are not trader behaviour.
const EXCLUDED_PATHS = ['/admin', '/agent', '/s/'];

// Not a visitor at all (owner, 2026-11-01): automated browsers, and our own
// test and screenshot runs, which set the flag below before the page loads.
// Nothing is queued or sent for them — not even the keepalive send on page
// close, which is what leaked six one-event sessions from headless runs.
export const NO_TRACK_KEY = 'maabar.growth.notrack';
const AUTOMATED_UA = /HeadlessChrome|Puppeteer|Playwright|Lighthouse|PageSpeed|GTmetrix|Googlebot|bingbot|YandexBot|DuckDuckBot|Baiduspider|Applebot|PetalBot|AhrefsBot|SemrushBot|facebookexternalhit|\bbot\b|crawler|spider/i;
export function isAutomated(nav = (typeof navigator !== 'undefined' ? navigator : {})) {
  try { return !!nav.webdriver || AUTOMATED_UA.test(nav.userAgent || ''); } catch { return false; }
}
function testRun() {
  try {
    if (typeof window === 'undefined') return false;
    if (window.__MAABAR_NO_TRACK__ === true) return true;
    // ?maabar_notrack=1 excludes this browser for good (stored locally, so it
    // holds signed out too — the team opens it once per device); =0 undoes it.
    const flag = new URLSearchParams(window.location.search).get('maabar_notrack');
    if (flag === '1') localStorage.setItem(NO_TRACK_KEY, '1');
    if (flag === '0') localStorage.removeItem(NO_TRACK_KEY);
    return localStorage.getItem(NO_TRACK_KEY) === '1';
  } catch { return false; }
}
let disabled = null;   // decided once per page load
export function trackingDisabled() {
  if (disabled === null) disabled = isAutomated() || testRun();
  return disabled;
}

// A page load that comes back from our own sign-in / OAuth / payment flow
// continues the visit: no new session, no new source (owner, 2026-11-01).
// The session is kept for up to RETURN_MS even past the 30 idle minutes —
// a payment at the bank can take a while.
const RETURN_MS = 2 * 60 * 60 * 1000;
let ownFlowReturn = null;
function returningFromOwnFlow() {
  if (ownFlowReturn === null) {
    try { ownFlowReturn = isOwnFlowReturn(document.referrer, window.location.pathname, window.location.hostname); } catch { ownFlowReturn = false; }
  }
  return ownFlowReturn;
}
// Leaving or backgrounding the page belongs to the visit that was open: it
// never opens a new session (a tab left open past 30 minutes produced
// sessions holding a single «غادر الصفحة»).
const PASSIVE = new Set(['page_hidden', 'app_backgrounded']);

let queue = [];
let timer = null;
let excludedUser = false;
let accessToken = null;
let memSession = null;
let memAnon = null;
let screen = { path: null, prev: null, since: Date.now(), maxScroll: 0 };
let navEntry = 'landing';
let started = false;
let warned = false;
let clarityTagged = null;   // the session id last sent to Clarity
let clarityLinked = null;   // our session | Clarity user | Clarity session, last recorded
let clarityWatching = false;
let clarityAnon = null;     // our browser id, re-sent with every new Clarity session

function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function anonId() {
  const id = getAnonId();
  if (id) return id;
  if (!memAnon) memAnon = uuid();   // storage blocked: stable for this page load only
  return memAnon;
}

function pathExcluded(path) {
  return EXCLUDED_PATHS.some((p) => (path || '').startsWith(p));
}

// One session per 30 idle minutes. Returns { id, isNew }, or null for a
// passive event when there is no session to attach it to.
function touchSession(now = Date.now(), passive = false) {
  let s = null;
  try { s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { s = memSession; }
  const has = !!(s && s.id);
  if (passive) return has ? { id: s.id, isNew: false } : null;
  const idle = has ? now - (s.last || 0) : Infinity;
  const isNew = !has || (idle > IDLE_MS && !(returningFromOwnFlow() && idle <= RETURN_MS));
  const next = { id: isNew ? uuid() : s.id, last: now };
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(next)); } catch { memSession = next; }
  return { id: next.id, isNew };
}

function deviceClass() {
  const w = (typeof window !== 'undefined' && window.innerWidth) || 0;
  if (w && w < 768) return 'mobile';
  if (w && w < 1024) return 'tablet';
  return 'desktop';
}

function referrerDomain() {
  try {
    if (!document.referrer || isInternalReferrer(document.referrer, window.location.hostname)) return null;
    return new URL(document.referrer).hostname || null;
  } catch { return null; }
}

// The operating system, as one of a fixed list of coarse names — never the
// raw user-agent string (owner, 2026-11-01: «سجل الجلسات» shows device and OS).
export function osName(ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '', touch = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0) {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touch > 1)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/CrOS/i.test(ua)) return 'ChromeOS';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'other';
}

export function sessionStartProps(loc = window.location) {
  const language = (document.documentElement.lang || '').slice(0, 5) || null;
  const props = {
    landing_path: loc.pathname,
    language,
    // From the language, not <html dir>: the first event is sent before the
    // app sets the page direction (seen live 2026-11-01: rtl:false on Arabic).
    rtl: /^ar/.test(language || ''),
    device_class: deviceClass(),
    os: osName(),
  };
  const ref = referrerDomain();
  if (ref) props.referrer_domain = ref;
  try {
    const q = new URLSearchParams(loc.search || '');
    for (const k of ATTR_KEYS) {
      const v = (q.get(k) || '').trim();
      if (v) props[k] = v.slice(0, 120);
    }
  } catch { /* no attribution is fine */ }
  return props;
}

function persist() {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-QUEUE_MAX))); } catch { /* memory only */ }
}

function enqueue(ev) {
  queue.push(ev);
  if (queue.length > QUEUE_MAX) queue = queue.slice(-QUEUE_MAX);
  persist();
  if (queue.length >= BATCH) flush();
  else if (!timer) timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
}

// Clarity: tag the recording with OUR browser and session ids, once per
// session, so a Growth OS list can open exactly those recordings. Custom tags
// (plain values, filterable in Clarity) plus identify (Clarity's own user /
// session linking). window.clarity is the queue stub initClarity() installs;
// without a Clarity id it does not exist and this does nothing.
function tagClarity(anon, sessionId) {
  try {
    if (clarityTagged === sessionId || typeof window.clarity !== 'function') return;
    clarityTagged = sessionId;
    clarityAnon = anon;
    window.clarity('set', 'maabar_anon', anon);
    window.clarity('set', 'maabar_session', sessionId);
    window.clarity('identify', anon, sessionId);
    // Clarity's own ids for this recording (owner, 2026-11-01): with them a
    // Growth OS link opens the exact recording in Clarity's player. Asked now
    // for this session, and kept registered (recall) so a new Clarity session
    // later in the visit is linked too. wait=false: answer even before
    // Clarity has decided to upload the full recording.
    window.clarity('metadata', onClarityMetadata, false, !clarityWatching);
    clarityWatching = true;
  } catch { /* never break the page */ }
}

export function onClarityMetadata(data) {
  try {
    const user = data && String(data.userId || ''); const session = data && String(data.sessionId || '');
    if (!clarityTagged || !user || !session || user.length > 64 || session.length > 64) return;
    const key = `${clarityTagged}|${user}|${session}`;
    if (key === clarityLinked) return;
    const prev = clarityLinked;
    const newClarity = !!prev && prev.slice(prev.indexOf('|') + 1) !== `${user}|${session}`;
    clarityLinked = key;
    // A new Clarity session (Clarity restarts on every in-app page change, and
    // without its cookies the restart is a new user and session) starts with
    // none of our tags: send them again, or the recording cannot be found by
    // our session id. Seen live 2026-11-01: four pages, four Clarity sessions,
    // only the first carried the tags.
    if (newClarity && clarityAnon && typeof window.clarity === 'function') {
      window.clarity('set', 'maabar_anon', clarityAnon);
      window.clarity('set', 'maabar_session', clarityTagged);
      window.clarity('identify', clarityAnon, clarityTagged);
    }
    track('clarity_session_linked', { clarity_user: user, clarity_session: session });
  } catch { /* never break the page */ }
}

// Record one event. Props are checked again on the server; unknown keys are dropped there.
export function track(eventName, props = {}) {
  try {
    if (excludedUser || typeof window === 'undefined' || trackingDisabled()) return;
    const path = window.location.pathname;
    if (pathExcluded(path)) return;
    const now = Date.now();
    const s = touchSession(now, PASSIVE.has(eventName));
    if (!s) return;
    const base = { platform: 'web', app_version: APP_VERSION, anon_id: anonId(), session_id: s.id, screen: path };
    tagClarity(base.anon_id, s.id);
    if (s.isNew && eventName !== 'session_started') {
      enqueue({ ...base, event_id: uuid(), event_name: 'session_started', occurred_at: new Date(now).toISOString(), props: sessionStartProps() });
    }
    enqueue({ ...base, event_id: uuid(), event_name: eventName, occurred_at: new Date(now).toISOString(), props: props || {} });
  } catch { /* never break the page */ }
}

// Send up to one batch. A failed batch goes back to the front of the queue.
export async function flush() {
  if (trackingDisabled()) { queue = []; return; }
  if (!queue.length) return;
  const batch = queue.splice(0, BATCH);
  persist();
  try {
    const { error } = await sb.rpc('track_events', { p_events: batch });
    if (error) throw error;
    if (queue.length) flush();
  } catch (err) {
    queue = batch.concat(queue).slice(-QUEUE_MAX);
    persist();
    if (!warned) { warned = true; console.warn('[growthTracker] track_events failed:', err?.message || err); }
  }
}

// On tab hide: the page may be gone before a normal request returns, so this
// one uses fetch keepalive with the same headers the client would send.
function flushOnHide() {
  if (trackingDisabled()) { queue = []; return; }
  if (!queue.length) return;
  const batch = queue.splice(0, BATCH);
  persist();
  try {
    fetch(`${SUPABASE_URL}/rest/v1/rpc/track_events`, {
      method: 'POST',
      keepalive: true,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken || SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ p_events: batch }),
    }).catch(() => { queue = batch.concat(queue).slice(-QUEUE_MAX); persist(); });
  } catch {
    queue = batch.concat(queue).slice(-QUEUE_MAX);
    persist();
  }
}

// Called on every route change from AppContent.
export function trackScreen(path) {
  try {
    if (path === screen.path) return;
    const previous = screen.path;
    screen = { path, prev: previous, since: Date.now(), maxScroll: 0 };
    track('screen_viewed', { previous_screen: previous, entry: previous ? navEntry : 'landing' });
    navEntry = 'nav';
  } catch { /* never break the page */ }
}

// Where the visitor came from to reach the current page. A page's own effects
// run before AppContent's trackScreen, so the current path may not be recorded
// yet — then the last recorded screen IS the one before.
export function cameFrom() {
  try { return screen.path === window.location.pathname ? screen.prev : screen.path; } catch { return null; }
}
// How far down the current screen has been scrolled (0–100).
export function screenScrollPct() { return screen.maxScroll; }

function onScroll() {
  try {
    const doc = document.documentElement;
    const room = doc.scrollHeight - window.innerHeight;
    const pct = room > 0 ? Math.min(100, Math.round((window.scrollY / room) * 100)) : 100;
    if (pct > screen.maxScroll) screen.maxScroll = pct;
  } catch { /* never break the page */ }
}

// Admin and super_admin accounts are the team, not traders.
export function setTrackingExcluded(excluded) {
  excludedUser = !!excluded;
  if (excludedUser) { queue = []; persist(); }
}

export function initGrowthTracker() {
  if (started || typeof window === 'undefined') return;
  started = true;
  try {
    // An automated browser or a test run: nothing to record, and a queue
    // left from an earlier page load is dropped rather than sent.
    if (trackingDisabled()) { try { localStorage.removeItem(QUEUE_KEY); } catch { /* nothing */ } return; }
    try { queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { queue = []; }
    if (!Array.isArray(queue)) queue = [];
    // Where this visit came from: first touch (30 days) and last non-direct touch.
    noteLanding();
    sb.auth.getSession().then(({ data }) => { accessToken = data?.session?.access_token || null; }).catch(() => {});
    sb.auth.onAuthStateChange((event, session) => {
      accessToken = session?.access_token || null;
      // A new account records where it came from; the server refuses any account older than 24 h.
      if (event === 'SIGNED_IN' && session?.user?.id) recordSignupAttribution(session.user.id, anonId());
    });
    window.addEventListener('popstate', () => { navEntry = 'back'; });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        track('page_hidden', { seconds_on_screen: Math.round((Date.now() - screen.since) / 1000) });
        flushOnHide();
      }
    });
    if (queue.length) flush();
  } catch { /* never break the page */ }
}

// Test seam.
export function __resetForTests() {
  queue = []; timer = null; excludedUser = false; accessToken = null; memSession = null; memAnon = null;
  screen = { path: null, prev: null, since: Date.now(), maxScroll: 0 }; navEntry = 'landing'; started = false; warned = false; clarityTagged = null; clarityLinked = null; clarityWatching = false; clarityAnon = null;
  disabled = null; ownFlowReturn = null;
}
export function __queue() { return queue; }
